import {
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { SessionStore } from 'app/core/services/session.store';
import { LegalDocumentSigningContainer } from 'app/legal-documents/components/legal-document-signing/legal-document-signing.container';
import { NotificationPopoverContainer } from 'app/notifications/components/notification-popover/notification-popover.container';
import { NavTab, NavTabComponent } from 'app/shared/components/nav-tab/nav-tab.component';
import { SomfyproLogoComponent } from 'app/shared/components/somfypro-logo/somfypro-logo.component';
import { ToasterHostComponent } from 'app/shared/components/toaster/toaster-host/toaster-host.component';
import { APP_CONFIG } from 'app/shared/injectionTokens';
import { AppConfig } from 'app/shared/models/AppConfig';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { UISocketService } from 'app/shared/services/ui.socket.service';
import { getSomfyProUrl } from 'app/utils/url';

import { IAdvizeComponent } from '../iAdvize/iAdvize.component';
import { SideNavComponent } from '../side-nav/side-nav.component';

const DASHBOARD_ROUTES = ['/sites', '/alerts', '/link-requests'];

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SomfyproLogoComponent,
        NavTabComponent,
        NotificationPopoverContainer,
        SideNavComponent,
        RouterOutlet,
        RouterLink,
        ToasterHostComponent,
        IAdvizeComponent,
        LegalDocumentSigningContainer,
    ],
})
export class HeaderComponent {
    private readonly uiSocketService = inject(UISocketService);
    private readonly router = inject(Router);
    private readonly session = inject(SessionStore);
    private readonly analyticService = inject(AnalyticsService);
    private readonly appConfig = inject<AppConfig>(APP_CONFIG);
    private readonly translate = inject(TranslateService);

    protected readonly userLogin = computed(() => this.session.user() ?? undefined);
    protected readonly somfyProUrl = computed(() => {
        const user = this.session.user();
        return user ? getSomfyProUrl(user.country, this.appConfig.name) : undefined;
    });
    private readonly contactPageUrl = this.session.contactPageUrl;

    private readonly currentUrl = signal(this.router.url);

    private readonly activeTab = computed<'dashboard' | 'notifications' | null>(() => {
        const url = this.currentUrl();
        if (url.startsWith('/notifications')) return 'notifications';
        if (DASHBOARD_ROUTES.some((route) => url.startsWith(route))) return 'dashboard';
        return null;
    });

    protected readonly notificationsActive = computed(() => this.activeTab() === 'notifications');

    protected readonly tabs = computed<NavTab[]>(() => {
        this.session.language();
        const contactPageUrl = this.contactPageUrl();

        const tabs: NavTab[] = [
            {
                label: this.translate.instant('DASHBOARD'),
                routerLink: '/sites',
                active: this.activeTab() === 'dashboard',
            },
        ];

        if (contactPageUrl) {
            tabs.push({
                label: this.translate.instant('SUPPORT'),
                href: contactPageUrl,
                onClick: () => this.analyticService.accessGlobalMenuDocument('assistance'),
            });
        }

        return tabs;
    });

    constructor() {
        this.uiSocketService.initSocket();

        const subscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.currentUrl.set(event.urlAfterRedirects);
            }
        });
        inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
    }
}
